import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Form,
  Input,
  Button,
  Space,
  Table,
  Select,
  InputNumber,
  Typography,
  message,
  Alert,
  Modal,
  Checkbox,
  Card,
  Spin
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  SaveOutlined,
  InboxOutlined,
  PrinterOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { stockService, StockLot } from '../../services/stockService';
import { CustomerSelect } from '../../components/customers';
import StockOutPrintModal from '../../components/stock-out/StockOutPrintModal';
import { useAuthStore } from '../../stores/authStore';
import { useQuery, useQueryClient } from 'react-query';
import api from '../../services/api';
import { useResponsive } from '../../hooks/useResponsive';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface StockOutItem {
  id?: number;
  voucher_id?: number;
  medicine_id?: number;
  medicine_name: string;
  medicine_code?: string;
  quantity: number;
  unit: string;
  unit_price: number;
  lot_id?: number;
  lot_number?: string;
  expiry_date?: string;
  total_amount?: number;
  notes?: string;
  available_stock?: number;
  lot_quantity?: number;
  is_free_product?: boolean;
}

interface StockOutRecord {
  id: number;
  reference_number?: string;
  voucher_number?: string;
  customer_id?: number;
  customer_name?: string;
  customer_phone?: string;
  customer_address?: string;
  total_items?: number;
  total_amount?: number;
  status?: 'pending' | 'completed' | 'cancelled';
  created_at?: string;
  completed_at?: string;
  created_by?: string;
  notes?: string;
  items?: StockOutItem[];
  voucher?: {
    id: number;
    voucher_number: string;
    customer_id?: number;
    customer_name: string;
    customer_phone: string;
    customer_address: string;
    total_amount: number;
    notes: string;
    created_by: string;
    created_at: string;
    status: string;
  };
}

interface Medicine {
  id: number;
  name: string;
  code: string;
  unit: string;
  current_stock: number;
  min_stock_level: number;
  max_stock_level: number;
}

const StockOutFormPage: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const isEditMode = !!id;
  const [form] = Form.useForm();
  const [items, setItems] = useState<StockOutItem[]>([]);
  const [selectedMedicine, setSelectedMedicine] = useState<Medicine | null>(null);
  const [selectedStockLots, setSelectedStockLots] = useState<StockLot[]>([]);
  const [itemForm] = Form.useForm();
  const [customers, setCustomers] = useState<any[]>([]);
  const [initialFormValues, setInitialFormValues] = useState<any>(null);
  const [initialItems, setInitialItems] = useState<StockOutItem[]>([]);
  const [isPromotion, setIsPromotion] = useState(false);
  const [printModalVisible, setPrintModalVisible] = useState(false);
  const [printData, setPrintData] = useState<any>(null);
  const [freeProductsHistoryVisible, setFreeProductsHistoryVisible] = useState(false);
  const [medicineLotsMap, setMedicineLotsMap] = useState<Map<number, StockLot[]>>(new Map());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user } = useAuthStore();
  const { screenWidth } = useResponsive();
  const queryClient = useQueryClient();

  // Fetch medicines
  const { data: medicines = [] } = useQuery(
    'medicines',
    () => api.get('/api/medicines').then(res => res.data.data),
    {
      staleTime: 10 * 60 * 1000,
    }
  );

  // Fetch detail record for editing
  const { data: editDetailRecord, isLoading: isLoadingDetail } = useQuery<StockOutRecord>(
    ['stock-out-edit-detail', id],
    () => stockService.getStockOutVoucherDetail(Number(id!)),
    {
      enabled: isEditMode && !!id,
      staleTime: 1 * 60 * 1000,
    }
  );

  // Lưu sản phẩm tự do vào localStorage
  const saveFreeProductsToLocalStorage = (freeProducts: StockOutItem[]) => {
    try {
      const existingData = JSON.parse(localStorage.getItem('freeProducts') || '[]');
      const newProducts = freeProducts.map(product => ({
        ...product,
        saved_at: new Date().toISOString(),
        saved_by: user?.username || 'Unknown'
      }));
      
      const updatedData = [...newProducts, ...existingData];
      const limitedData = updatedData.slice(0, 100);
      
      localStorage.setItem('freeProducts', JSON.stringify(limitedData));
    } catch (error) {
      console.error('Error saving free products to localStorage:', error);
    }
  };

  // Lấy danh sách sản phẩm tự do từ localStorage
  const getFreeProductsFromLocalStorage = (): StockOutItem[] => {
    try {
      const data = localStorage.getItem('freeProducts');
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('Error loading free products from localStorage:', error);
      return [];
    }
  };

  // Load data when editing
  useEffect(() => {
    if (isEditMode && editDetailRecord && !isLoadingDetail) {
      const voucherData = editDetailRecord.voucher || editDetailRecord;
      const itemsData = editDetailRecord.items || [];
      
      form.setFieldsValue({
        customer_id: voucherData.customer_id || null,
        customer_name: voucherData.customer_name,
        customer_phone: voucherData.customer_phone,
        customer_address: voucherData.customer_address,
        notes: voucherData.notes
      });
      
      if (voucherData.customer_name && customers.length > 0) {
        const existingCustomer = customers.find(c => c.name === voucherData.customer_name);
        if (!existingCustomer) {
          setCustomers(prev => [...prev, {
            id: 0,
            name: voucherData.customer_name,
            phone: voucherData.customer_phone,
            address: voucherData.customer_address
          }]);
        }
      }
      
      const mappedItems = itemsData.map(item => ({
        ...item,
        id: item.id,
        total_amount: item.quantity * item.unit_price,
        available_stock: item.available_stock || 0
      }));
      
      setItems(mappedItems);
      
      // Load danh sách lô cho các items
      const loadLotsForItems = async () => {
        const lotsMap = new Map<number, StockLot[]>();
        const uniqueMedicineIds = new Set(
          itemsData
            .filter(item => item.medicine_id && !item.is_free_product)
            .map(item => item.medicine_id!)
        );
        
        for (const medicineId of uniqueMedicineIds) {
          try {
            const stockLots = await stockService.getMedicineStock(medicineId);
            lotsMap.set(medicineId, stockLots);
          } catch (error) {
            console.error(`Error loading lots for medicine ${medicineId}:`, error);
            lotsMap.set(medicineId, []);
          }
        }
        
        setMedicineLotsMap(lotsMap);
      };
      
      loadLotsForItems();
    } else if (!isEditMode) {
      form.resetFields();
      setItems([]);
      setMedicineLotsMap(new Map());
    }
  }, [isEditMode, editDetailRecord, form, isLoadingDetail, customers.length]);

  // Lưu snapshot dữ liệu ban đầu
  useEffect(() => {
    if (!isLoadingDetail) {
      setInitialFormValues(form.getFieldsValue());
      setInitialItems([...items]);
    }
  }, [isLoadingDetail]);

  // Cảnh báo khi đóng trình duyệt nếu có dữ liệu chưa lưu
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty()) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [form, items, initialFormValues, initialItems]);

  // Hàm kiểm tra dữ liệu đã thay đổi chưa
  const isDirty = () => {
    if (!initialFormValues) return false;
    const currentForm = form.getFieldsValue();
    return (
      JSON.stringify(currentForm) !== JSON.stringify(initialFormValues) ||
      JSON.stringify(items) !== JSON.stringify(initialItems)
    );
  };

  const handleAddItem = () => {
    itemForm.validateFields().then((values) => {
      const medicine = medicines.find((m: Medicine) => m.id === values.medicine_id);
      const selectedLot = selectedStockLots.find(lot => lot.lot_id === values.lot_id);
      
      if (!medicine || !selectedLot) return;

      const newItem: StockOutItem = {
        medicine_id: values.medicine_id,
        medicine_name: medicine.name,
        medicine_code: medicine.code,
        quantity: values.quantity,
        unit: medicine.unit,
        unit_price: isPromotion ? 0 : values.unit_price,
        lot_id: values.lot_id,
        lot_number: selectedLot.lot_number,
        expiry_date: selectedLot.expiry_date,
        total_amount: values.quantity * (isPromotion ? 0 : values.unit_price),
        notes: values.notes || '',
        available_stock: selectedLot.available_quantity,
        is_free_product: false
      };

      setItems([...items, newItem]);
      
      // Lưu danh sách lô vào medicineLotsMap để có thể thay đổi sau này
      if (selectedStockLots.length > 0) {
        setMedicineLotsMap(prev => new Map(prev).set(values.medicine_id, selectedStockLots));
      }
      
      itemForm.resetFields();
      setSelectedMedicine(null);
      setSelectedStockLots([]);
      setIsPromotion(false);
    });
  };

  const handleRemoveItem = (index: number) => {
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems);
  };

  const handleMedicineChange = async (medicineId: number) => {
    const medicine = medicines.find((m: Medicine) => m.id === medicineId);
    setSelectedMedicine(medicine || null);
    setSelectedStockLots([]);
    
    if (medicine) {
      try {
        const stockLots = await stockService.getMedicineStock(medicineId);
        setSelectedStockLots(stockLots);
        
        itemForm.setFieldsValue({
          unit: medicine.unit,
          available_stock: medicine.current_stock
        });
      } catch (error) {
        console.error('Error fetching medicine stock:', error);
        message.error('Không thể lấy thông tin tồn kho');
      }
    }
  };

  const handlePrint = async () => {
    if (items.length === 0) {
      message.error('Vui lòng thêm ít nhất một sản phẩm để in phiếu');
      return;
    }

    const formValues = form.getFieldsValue();
    const totalAmount = items.reduce((sum, item) => sum + (item.total_amount || 0), 0);
    
    // Lấy mã phiếu: ưu tiên từ voucher trong editDetailRecord (khi sửa), sau đó là voucher_number, reference_number
    let voucherNumber: string | undefined;
    if (isEditMode && editDetailRecord) {
      // Khi sửa phiếu, lấy mã phiếu từ voucher object (cấu trúc từ API detail)
      voucherNumber = editDetailRecord.voucher?.voucher_number 
        || editDetailRecord.voucher_number 
        || editDetailRecord.reference_number;
    }
    
    // Nếu không có mã phiếu (chế độ tạo mới), lấy mã phiếu tiếp theo
    if (!voucherNumber) {
      try {
        voucherNumber = await stockService.getNextStockOutVoucherNumber();
      } catch (error) {
        console.error('Error getting next voucher number:', error);
        // Fallback nếu có lỗi
        voucherNumber = 'DRAFT-' + Date.now();
      }
    }
    
    // Lấy dữ liệu từ voucher object nếu đang sửa (từ API detail response)
    const voucherData = isEditMode && editDetailRecord?.voucher 
      ? editDetailRecord.voucher 
      : (isEditMode && editDetailRecord ? editDetailRecord : null);
    
    const printData = {
      voucher: {
        id: voucherData?.id || editDetailRecord?.id || 0,
        voucher_number: voucherNumber,
        customer_name: formValues.customer_name || '',
        customer_phone: formValues.customer_phone || '',
        customer_address: formValues.customer_address || '',
        total_amount: totalAmount,
        status: voucherData?.status || editDetailRecord?.status || 'pending',
        notes: formValues.notes || '',
        created_at: voucherData?.created_at || editDetailRecord?.created_at || new Date().toISOString(),
        created_by: voucherData?.created_by || editDetailRecord?.created_by || user?.username || 'User',
        created_by_name: user?.full_name || user?.username || 'User'
      },
      items: items.map(item => ({
        id: item.id || 0,
        medicine_id: item.medicine_id || 0,
        medicine_name: item.medicine_name,
        medicine_code: item.medicine_code || 'N/A',
        quantity: item.quantity,
        unit: item.unit,
        unit_price: item.unit_price,
        lot_id: item.lot_id || 0,
        lot_number: item.lot_number || 'N/A',
        expiry_date: item.expiry_date || '',
        total_amount: item.total_amount || (item.quantity * item.unit_price),
        notes: item.notes || '',
        available_stock: item.available_stock || 0
      }))
    };

    setPrintData(printData);
    setPrintModalVisible(true);
  };

  const handleQuantityChange = (quantity: number | null) => {
    if (quantity !== null) {
      const unitPrice = itemForm.getFieldValue('unit_price') || 0;
      itemForm.setFieldsValue({
        total_amount: quantity * unitPrice
      });
    }
  };

  const handleUnitPriceChange = (unitPrice: string | number | null) => {
    if (unitPrice !== null && typeof unitPrice === 'number') {
      const quantity = itemForm.getFieldValue('quantity') || 0;
      itemForm.setFieldsValue({
        total_amount: quantity * unitPrice
      });
    }
  };

  const handleCustomersLoaded = (customersList: any[]) => {
    setCustomers(customersList);
  };

  const updateItemInTable = (index: number, field: keyof StockOutItem, value: any) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    
    if (field === 'quantity' || field === 'unit_price') {
      newItems[index].total_amount = (newItems[index].quantity || 0) * (newItems[index].unit_price || 0);
    }
    
    setItems(newItems);
  };

  const handleLotChange = async (index: number, lotId: number) => {
    const item = items[index];
    if (!item || !item.medicine_id) return;
    
    // Lấy danh sách lô nếu chưa có
    let lots = medicineLotsMap.get(item.medicine_id);
    if (!lots || lots.length === 0) {
      try {
        lots = await stockService.getMedicineStock(item.medicine_id);
        setMedicineLotsMap(prev => new Map(prev).set(item.medicine_id!, lots));
      } catch (error) {
        console.error('Error loading lots:', error);
        message.error('Không thể tải danh sách lô');
        return;
      }
    }
    
    const selectedLot = lots.find(lot => lot.lot_id === lotId);
    if (selectedLot) {
      const newItems = [...items];
      newItems[index] = {
        ...newItems[index],
        lot_id: selectedLot.lot_id,
        lot_number: selectedLot.lot_number,
        expiry_date: selectedLot.expiry_date,
        available_stock: selectedLot.available_quantity
      };
      setItems(newItems);
    }
  };

  const columns = [
    {
      title: 'STT',
      key: 'index',
      width: 60,
      fixed: 'left' as const,
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: 'Tên sản phẩm',
      dataIndex: 'medicine_name',
      key: 'medicine_name',
      width: 200,
      minWidth: 180,
      render: (text: string, record: StockOutItem, index: number) => (
        <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {record.is_free_product ? (
            <Input
              value={text || ''}
              onChange={(e) => updateItemInTable(index, 'medicine_name', e.target.value)}
              placeholder="Tên sản phẩm"
              size="small"
            />
          ) : (
            <div>
              <div style={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{text || 'N/A'}</div>
              <div style={{ fontSize: '12px', color: '#666', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                Mã: {record.medicine_code || 'N/A'}
              </div>
            </div>
          )}
          {record.is_free_product && (
            <div style={{ fontSize: '12px', color: '#1890ff', fontWeight: '500', marginTop: '4px', whiteSpace: 'nowrap' }}>
              (Ngoài hệ thống)
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Số lượng',
      key: 'quantity',
      width: 130,
      render: (_: any, record: StockOutItem, index: number) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <InputNumber
            min={1}
            value={record.quantity || 0}
            onChange={(value) => updateItemInTable(index, 'quantity', value || 1)}
            size="small"
            style={{ flex: 1, minWidth: 0 }}
          />
          {record.is_free_product ? (
            <Input
              value={record.unit || ''}
              onChange={(e) => updateItemInTable(index, 'unit', e.target.value)}
              placeholder="Đơn vị"
              size="small"
              style={{ width: '80px', fontSize: '12px' }}
            />
          ) : (
            <div style={{ fontSize: '12px', color: '#666', whiteSpace: 'nowrap', minWidth: '50px' }}>
              {record.unit || 'N/A'}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Đơn giá',
      key: 'unit_price',
      width: 160,
      render: (_: any, record: StockOutItem, index: number) => (
        <InputNumber
          min={0}
          value={record.unit_price || 0}
          onChange={(value) => updateItemInTable(index, 'unit_price', value || 0)}
          formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          parser={(value: string | undefined) => value ? Number(value.replace(/\$\s?|(,*)/g, '')) : 0}
          size="small"
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Thành tiền',
      key: 'total_amount',
      width: 150,
      render: (_: any, record: StockOutItem) => (
        <div style={{ textAlign: 'right', fontWeight: 500, padding: '4px 8px', whiteSpace: 'nowrap' }}>
          {(record.total_amount || 0).toLocaleString('vi-VN')}
        </div>
      ),
    },
    {
      title: 'Số lô',
      key: 'lot_number',
      width: 180,
      render: (_: any, record: StockOutItem, index: number) => (
        <div>
          {record.is_free_product ? (
            <div style={{ textAlign: 'center', color: '#999', fontSize: '12px', whiteSpace: 'nowrap' }}>
              Sản phẩm ngoài hệ thống
            </div>
          ) : isEditMode && record.medicine_id ? (
            <Select
              value={record.lot_id || undefined}
              onChange={(value) => handleLotChange(index, value)}
              style={{ width: '100%' }}
              size="small"
              showSearch
              filterOption={(input, option: any) => {
                const children = option?.children;
                if (typeof children === 'string') {
                  return children.toLowerCase().includes(input.toLowerCase());
                }
                return false;
              }}
              placeholder="Chọn lô"
            >
              {(medicineLotsMap.get(record.medicine_id) || []).map(lot => (
                <Option key={lot.lot_id} value={lot.lot_id}>
                  {lot.lot_number} - HSD: {dayjs(lot.expiry_date).format('DD/MM/YYYY')} (Còn: {lot.available_quantity})
                </Option>
              ))}
            </Select>
          ) : (
            <div>
              <div style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                {record.lot_number || 'N/A'}
              </div>
              <div style={{ fontSize: '10px', color: '#666', whiteSpace: 'nowrap' }}>
                HSD: {record.expiry_date ? dayjs(record.expiry_date).format('DD/MM/YYYY') : 'N/A'}
              </div>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 90,
      fixed: 'right' as const,
      render: (_: any, __: any, index: number) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleRemoveItem(index)}
          size="small"
        />
      ),
    },
  ];

  const totalAmount = items.reduce((sum, item) => sum + (item.total_amount || 0), 0);

  const handleCancel = () => {
    if (isDirty()) {
      Modal.confirm({
        title: 'Bạn có chắc muốn thoát?',
        content: 'Dữ liệu chưa lưu sẽ bị mất. Bạn có muốn tiếp tục?',
        okText: 'Thoát',
        cancelText: 'Ở lại',
        onOk: () => {
          navigate('/stock-out');
        },
      });
    } else {
      navigate('/stock-out');
    }
  };

  const handleSubmit = async (values: any) => {
    if (items.length === 0) {
      message.error('Vui lòng thêm ít nhất một thuốc');
      return;
    }
    
    const invalidItems = items.filter(item => {
      if (item.is_free_product) {
        return !item.medicine_name?.trim() || !item.quantity || item.unit_price === null || item.unit_price === undefined || !item.unit?.trim();
      } else {
        return !item.medicine_id || !item.quantity || item.unit_price === null || item.unit_price === undefined || !item.lot_id;
      }
    });
    
    if (invalidItems.length > 0) {
      message.error('Một số sản phẩm thiếu thông tin bắt buộc. Vui lòng kiểm tra lại tên sản phẩm, số lượng, đơn giá và đơn vị tính.');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const submitData: any = {
        customer_id: values.customer_id || null,
        customer_name: values.customer_name,
        customer_phone: values.customer_phone,
        customer_address: values.customer_address,
        notes: values.notes || '',
        items: items.filter(item => !item.is_free_product).map(item => {
          const itemData: any = {
            medicine_id: item.medicine_id,
            quantity: item.quantity,
            unit_price: item.unit_price,
            lot_id: item.lot_id,
            lot_number: item.lot_number || 'N/A',
            notes: item.notes || ''
          };
          
          if (item.id) {
            itemData.id = item.id;
          }
          
          if (itemData.lot_id === undefined || itemData.lot_id === '') {
            itemData.lot_id = null;
          }
          
          return itemData;
        })
      };

      const freeProducts = items.filter(item => item.is_free_product);
      if (freeProducts.length > 0) {
        saveFreeProductsToLocalStorage(freeProducts);
        message.success(`Đã lưu ${freeProducts.length} sản phẩm ngoài hệ thống vào lịch sử`);
      }

      if (isEditMode && id) {
        const recordId = editDetailRecord?.voucher?.id || editDetailRecord?.id || Number(id);
        if (editDetailRecord?.voucher?.id) {
          submitData.voucher_id = editDetailRecord.voucher.id;
        }
        await stockService.updateStockOutVoucher(recordId, submitData);
        message.success('Cập nhật phiếu xuất kho thành công!');
      } else {
        await stockService.createStockOutVoucher(submitData);
        message.success('Tạo phiếu xuất kho thành công!');
      }
      
      // Invalidate query để tự động reload danh sách khi quay lại trang
      queryClient.invalidateQueries('stock-out');
      queryClient.invalidateQueries('inventory');
      
      navigate('/stock-out');
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Có lỗi xảy ra');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ 
      height: '100%',
      display: 'flex', 
      flexDirection: 'column',
      overflow: 'hidden',
      backgroundColor: '#f0f2f5'
    }}>
      {/* Header */}
      <Card 
        style={{ 
          margin: screenWidth >= 1200 ? '16px 16px 0 16px' : '12px 12px 0 12px',
          borderRadius: '8px 8px 0 0',
          flexShrink: 0
        }}
        bodyStyle={{ padding: screenWidth >= 1200 ? '16px 24px' : '12px 16px' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <Title level={4} style={{ margin: 0 }}>
              {isEditMode 
                ? `Sửa phiếu xuất kho ${editDetailRecord?.voucher?.voucher_number || editDetailRecord?.reference_number || ''}`
                : 'Tạo phiếu xuất kho'
              }
            </Title>
            {isLoadingDetail && (
              <Text type="secondary" style={{ fontSize: '14px' }}>
                (Đang tải dữ liệu...)
              </Text>
            )}
          </div>
        </div>
      </Card>

      {/* Content */}
      <div style={{ 
        flex: 1, 
        margin: screenWidth >= 1200 ? '0 16px 0 16px' : '0 12px 0 12px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        paddingBottom: '0'
      }}>
        <Card 
          style={{ 
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            borderRadius: '0 0 8px 8px'
          }}
          bodyStyle={{ 
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            padding: screenWidth >= 1200 ? '24px 24px 0 24px' : screenWidth >= 992 ? '20px 20px 0 20px' : '16px 16px 0 16px'
          }}
        >
          <Spin spinning={isLoadingDetail}>
            <Form
              form={form}
              layout="vertical"
              style={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
              }}
              onFinish={handleSubmit}
            >
              {/* Two Column Layout */}
              <div style={{ 
                flex: 1, 
                display: 'flex', 
                flexDirection: screenWidth >= 1200 ? 'row' : 'column',
                gap: screenWidth >= 1200 ? '24px' : screenWidth >= 992 ? '20px' : '16px',
                minHeight: 0,
                overflow: 'hidden'
              }}>
                {/* Left Column - Medicine Details */}
                <div style={{ 
                  flex: 1, 
                  display: 'flex', 
                  flexDirection: 'column',
                  minWidth: 0,
                  overflow: 'hidden'
                }}>
                  {/* Add Item Form */}
                  <div style={{ 
                    flexShrink: 0,
                    padding: '16px', 
                    border: 'none',
                    borderRadius: '6px',
                    marginBottom: '16px',
                    backgroundColor: '#fafafa'
                  }}>
                    <Title level={5} style={{ textAlign: 'left' }}>Thêm sản phẩm</Title>
                    
                    <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                      <Button 
                        type="link" 
                        size="small"
                        onClick={() => setFreeProductsHistoryVisible(true)}
                        style={{ padding: 0, height: 'auto', fontSize: '12px' }}
                      >
                        Xem lịch sử ({getFreeProductsFromLocalStorage().length})
                      </Button>
                      <Button 
                        type="dashed" 
                        size="small"
                        onClick={() => {
                          const newItem: StockOutItem = {
                            medicine_name: '',
                            quantity: 1,
                            unit: 'hộp',
                            unit_price: 0,
                            total_amount: 0,
                            notes: '',
                            is_free_product: true
                          };
                          setItems([...items, newItem]);
                        }}
                        style={{ fontSize: '12px' }}
                      >
                        + Thêm sản phẩm ngoài hệ thống
                      </Button>
                    </div>

                    <Form form={itemForm} layout={screenWidth >= 1200 ? "inline" : "vertical"}>
                      <Form.Item
                        name="medicine_id"
                        rules={[{ required: true, message: 'Chọn thuốc' }]}
                      >
                        <Select
                          placeholder="Chọn thuốc"
                          style={{ width: screenWidth >= 1200 ? 200 : '100%' }}
                          onChange={handleMedicineChange}
                          showSearch
                          filterOption={(input, option: any) => {
                            const children = option?.children;
                            if (typeof children === 'string') {
                              return children.toLowerCase().includes(input.toLowerCase());
                            }
                            return false;
                          }}
                        >
                          {medicines.map((medicine: any) => (
                            <Option key={medicine.id} value={medicine.id}>
                              {medicine.name} ({medicine.code})
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>

                      <Form.Item
                        name="lot_id"
                        rules={[{ required: true, message: 'Chọn lô thuốc' }]}
                      >
                        <Select
                          placeholder="Chọn lô thuốc"
                          style={{ width: screenWidth >= 1200 ? 200 : '100%' }}
                          showSearch
                          filterOption={(input, option: any) => {
                            const children = option?.children;
                            if (typeof children === 'string') {
                              return children.toLowerCase().includes(input.toLowerCase());
                            }
                            return false;
                          }}
                        >
                          {selectedStockLots.map(lot => (
                            <Option key={lot.lot_id} value={lot.lot_id}>
                              {lot.lot_number} - HSD: {dayjs(lot.expiry_date).format('DD/MM/YYYY')} (Còn: {lot.available_quantity})
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>

                      <Form.Item 
                        name="quantity" 
                        rules={[{ required: true, message: 'Nhập số lượng' }]}
                      >
                        <InputNumber
                          placeholder="Số lượng"
                          min={1}
                          onChange={handleQuantityChange}
                          style={{ width: screenWidth >= 1200 ? 120 : '100%' }}
                        />
                      </Form.Item>

                      <Form.Item>
                        <Checkbox checked={isPromotion} onChange={e => setIsPromotion(e.target.checked)}>
                          Hàng khuyến mãi (giá = 0)
                        </Checkbox>
                      </Form.Item>

                      <Form.Item
                        name="unit_price"
                        rules={[
                          {
                            required: !isPromotion,
                            message: 'Nhập đơn giá'
                          }
                        ]}
                      >
                        <InputNumber
                          placeholder="Đơn giá"
                          min={0}
                          formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                          parser={(value: string | undefined) => value ? Number(value.replace(/\$\s?|,*/g, '')) : 0 as any}
                          onChange={handleUnitPriceChange}
                          style={{ width: screenWidth >= 1200 ? 150 : '100%' }}
                          disabled={isPromotion}
                          value={isPromotion ? 0 : undefined}
                        />
                      </Form.Item>

                      <Form.Item>
                        <Button
                          type="primary"
                          icon={<PlusOutlined />}
                          onClick={handleAddItem}
                          block={screenWidth < 1200}
                        >
                          Thêm
                        </Button>
                      </Form.Item>
                    </Form>

                    {selectedStockLots.length > 0 && (
                      <Alert
                        message={`Tồn kho theo lô: ${selectedStockLots.length} lô có sẵn`}
                        description={
                          <div style={{ textAlign: 'left' }}>
                            {selectedStockLots.map(lot => (
                              <div key={lot.lot_id} style={{ marginTop: '4px', textAlign: 'left' }}>
                                • Lô {lot.lot_number || 'N/A'}: {lot.available_quantity || 0} {selectedMedicine?.unit || 'N/A'} 
                                (HSD: {lot.expiry_date ? dayjs(lot.expiry_date).format('DD/MM/YYYY') : 'N/A'})
                              </div>
                            ))}
                          </div>
                        }
                        type="info"
                        showIcon
                        style={{ marginTop: '8px', textAlign: 'left' }}
                      />
                    )}
                    {selectedMedicine && selectedStockLots.length === 0 && (
                      <Alert
                        message={`Không có tồn kho cho thuốc này`}
                        type="warning"
                        showIcon
                        style={{ marginTop: '8px' }}
                      />
                    )}
                  </div>

                  {/* Items Table */}
                  <div style={{ 
                    flex: 1, 
                    overflow: 'hidden', 
                    minHeight: 0,
                    border: 'none',
                    borderRadius: '6px',
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    {items.length === 0 ? (
                      <div style={{ 
                        padding: '40px', 
                        textAlign: 'center', 
                        color: '#999',
                        border: 'none',
                        margin: '8px',
                        borderRadius: '6px'
                      }}>
                        <InboxOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
                        <div>Chưa có thuốc nào được thêm</div>
                        <div style={{ fontSize: '12px' }}>Vui lòng thêm thuốc từ form bên trên</div>
                      </div>
                    ) : (
                      <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
                        <Table
                          columns={columns}
                          dataSource={items}
                          rowKey={(_record, index) => index?.toString() || '0'}
                          pagination={false}
                          size="small"
                          bordered={false}
                          scroll={{ x: 930 }}
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Right Column - Other Information */}
                <div style={{ 
                  width: screenWidth >= 1200 ? '400px' : screenWidth >= 992 ? '350px' : '100%',
                  display: 'flex', 
                  flexDirection: 'column',
                  flexShrink: 0,
                  overflow: 'hidden'
                }}>
                  <div style={{ 
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '16px', 
                    border: 'none',
                    borderRadius: '6px',
                    backgroundColor: '#fafafa',
                    overflow: 'hidden'
                  }}>
                    {/* Customer Information */}
                    <div style={{ flexShrink: 0 }}>
                      <Form.Item
                        name="customer_id"
                        label="Chọn khách hàng"
                        rules={[{ required: true, message: 'Vui lòng chọn khách hàng' }]}
                        style={{ marginBottom: '16px' }}
                      >
                        <CustomerSelect
                          placeholder="Chọn khách hàng từ danh sách"
                          style={{ textAlign: 'left' }}
                          onChange={(value, customer) => {
                            if (customer) {
                              form.setFieldsValue({
                                customer_id: value,
                                customer_name: customer.name || '',
                                customer_phone: customer.phone || '',
                                customer_address: customer.address || ''
                              });
                            }
                          }}
                          onCustomersLoaded={handleCustomersLoaded}
                        />
                      </Form.Item>

                      <Form.Item name="customer_name" style={{ display: 'none' }}>
                        <Input />
                      </Form.Item>
                      <Form.Item name="customer_phone" style={{ display: 'none' }}>
                        <Input />
                      </Form.Item>
                      <Form.Item name="customer_address" style={{ display: 'none' }}>
                        <Input />
                      </Form.Item>

                      <Form.Item
                        name="customer_phone"
                        label="Số điện thoại"
                        rules={[{ required: true, message: 'Vui lòng nhập số điện thoại' }]}
                        style={{ marginBottom: '16px' }}
                      >
                        <Input 
                          placeholder="Số điện thoại sẽ tự động điền khi chọn khách hàng"
                          style={{ width: '100%' }}
                        />
                      </Form.Item>

                      <Form.Item
                        name="customer_address"
                        label="Địa chỉ"
                        rules={[{ required: true, message: 'Vui lòng nhập địa chỉ' }]}
                        style={{ marginBottom: '16px' }}
                      >
                        <TextArea 
                          rows={3} 
                          placeholder="Địa chỉ sẽ tự động điền khi chọn khách hàng"
                          style={{ width: '100%', resize: 'none' }}
                        />
                      </Form.Item>
                    </div>

                    {/* Summary */}
                    <div style={{ 
                      flexShrink: 0,
                      padding: '16px',
                      border: 'none',
                      borderRadius: '6px',
                      marginBottom: '16px',
                      backgroundColor: '#f0f8ff',
                      textAlign: 'left'
                    }}>
                      <Title level={5} style={{ marginBottom: '12px', fontSize: '16px', textAlign: 'left' }}>Tổng kết</Title>
                      <div style={{ marginBottom: '8px' }}>
                        <Text>Số lượng thuốc: </Text>
                        <Text strong>{items.length}</Text>
                      </div>
                      <div style={{ marginBottom: '8px' }}>
                        <Text>Tổng số lượng: </Text>
                        <Text strong>{items.reduce((sum, item) => sum + (item.quantity || 0), 0)}</Text>
                      </div>
                      <div style={{ height: '1px', backgroundColor: '#e8e8e8', margin: '12px 0' }} />
                      <div style={{ textAlign: 'right' }}>
                        <Text strong style={{ fontSize: '16px', color: '#52c41a' }}>
                          Tổng tiền: {(totalAmount || 0).toLocaleString('vi-VN')} VNĐ
                        </Text>
                      </div>
                    </div>

                    {/* Notes */}
                    <div style={{ 
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      minHeight: 0,
                      overflow: 'hidden',
                      marginBottom: '16px'
                    }}>
                      <Form.Item 
                        name="notes" 
                        label="Ghi chú"
                        style={{ 
                          flex: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          marginBottom: 0
                        }}
                      >
                        <TextArea 
                          rows={3} 
                          placeholder="Nhập ghi chú (nếu có)"
                          style={{ 
                            resize: 'none',
                            minHeight: '60px'
                          }}
                        />
                      </Form.Item>
                    </div>

                    {/* Action Buttons */}
                    <div style={{ 
                      flexShrink: 0,
                      textAlign: 'right', 
                      paddingTop: '16px',
                      borderTop: 'none'
                    }}>
                      <Space>
                        <Button onClick={handleCancel} disabled={isSubmitting}>
                          Hủy
                        </Button>
                        <Button 
                          icon={<PrinterOutlined />} 
                          onClick={handlePrint}
                          disabled={items.length === 0 || isSubmitting}
                        >
                          In phiếu
                        </Button>
                        <Button 
                          type="primary" 
                          icon={<SaveOutlined />} 
                          htmlType="submit"
                          loading={isSubmitting}
                        >
                          {isEditMode ? 'Cập nhật' : 'Lưu phiếu'}
                        </Button>
                      </Space>
                    </div>
                  </div>
                </div>
              </div>
            </Form>
          </Spin>
        </Card>
      </div>
      
      {/* Print Modal */}
      <StockOutPrintModal
        visible={printModalVisible}
        onCancel={() => setPrintModalVisible(false)}
        data={printData}
      />

      {/* Free Products History Modal */}
      <Modal
        title="Lịch sử sản phẩm ngoài hệ thống"
        open={freeProductsHistoryVisible}
        onCancel={() => setFreeProductsHistoryVisible(false)}
        width={800}
        footer={[
          <Button key="close" onClick={() => setFreeProductsHistoryVisible(false)}>
            Đóng
          </Button>
        ]}
      >
        <Table
          columns={[
            {
              title: 'STT',
              key: 'index',
              width: 60,
              render: (_: any, __: any, index: number) => index + 1,
            },
            {
              title: 'Tên sản phẩm',
              dataIndex: 'medicine_name',
              key: 'medicine_name',
              render: (text: string) => (
                <div style={{ fontWeight: 500 }}>{text}</div>
              ),
            },
            {
              title: 'Số lượng',
              key: 'quantity',
              width: 100,
              render: (_: any, record: StockOutItem) => (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontWeight: 500 }}>{record.quantity}</div>
                  <div style={{ fontSize: '12px', color: '#666' }}>{record.unit}</div>
                </div>
              ),
            },
            {
              title: 'Đơn giá',
              key: 'unit_price',
              width: 120,
              render: (_: any, record: StockOutItem) => (
                <div style={{ textAlign: 'right' }}>
                  {(record.unit_price || 0).toLocaleString('vi-VN')} VNĐ
                </div>
              ),
            },
            {
              title: 'Thành tiền',
              key: 'total_amount',
              width: 130,
              render: (_: any, record: StockOutItem) => (
                <div style={{ textAlign: 'right', fontWeight: 500 }}>
                  {(record.total_amount || 0).toLocaleString('vi-VN')} VNĐ
                </div>
              ),
            },
            {
              title: 'Người tạo',
              key: 'saved_by',
              width: 120,
              render: (_: any, record: any) => (
                <div style={{ fontSize: '12px' }}>
                  {record.saved_by || 'Unknown'}
                </div>
              ),
            },
            {
              title: 'Thời gian',
              key: 'saved_at',
              width: 150,
              render: (_: any, record: any) => (
                <div style={{ fontSize: '12px' }}>
                  {record.saved_at ? dayjs(record.saved_at).format('DD/MM/YYYY HH:mm') : 'N/A'}
                </div>
              ),
            },
            {
              title: 'Thao tác',
              key: 'actions',
              width: 100,
              render: (_: any, record: StockOutItem) => (
                <Button
                  type="link"
                  size="small"
                  onClick={() => {
                    const newItem: StockOutItem = {
                      ...record,
                      is_free_product: true
                    };
                    setItems([...items, newItem]);
                    setFreeProductsHistoryVisible(false);
                    message.success('Đã thêm sản phẩm vào phiếu');
                  }}
                >
                  Thêm lại
                </Button>
              ),
            },
          ]}
          dataSource={getFreeProductsFromLocalStorage()}
          rowKey={(_, index) => `free-product-${index}`}
          pagination={{ pageSize: 10 }}
          size="small"
          scroll={{ x: 700 }}
          locale={{
            emptyText: 'Chưa có sản phẩm ngoài hệ thống nào được lưu'
          }}
        />
      </Modal>
    </div>
  );
};

export default StockOutFormPage;

